import { type Employee } from "@finway-group/shared/lib/models"
import { useQuery } from "@tanstack/react-query"
import { queryClient } from "~Shared/config/reactQueryConfig"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { useCompany } from "~Shared/hooks/company.hooks"
import { CacheEnum } from "~Shared/queries/consts"
import { EmployeeService } from "~Shared/services/employee.service"
import { filterEmployees } from "~Shared/utils/employee.utils"
import { employeeSorter } from "~Shared/utils/helper.utils"

export const useEmployee = (id?: string) =>
    useQuery({
        queryKey: [CacheEnum.EMPLOYEE, id],
        queryFn: ({ signal }) => EmployeeService.fetchEmployeeById(id as string, signal),
        select: (data) => data ?? undefined,
        enabled: !!id,
    })

export const fetchEmployee = (employeeId: string | undefined) => {
    if (!employeeId) {
        return undefined
    }
    return queryClient.fetchQuery({
        queryKey: [CacheEnum.EMPLOYEE, employeeId],
        queryFn: ({ signal }) => EmployeeService.fetchEmployeeById(employeeId, signal),
    })
}

interface EmployeeQuery {
    excludeDeleted?: boolean
    includeAutoApprover?: boolean
    searchValue?: string
    companyId?: string
}

const DEFAULT_EMPLOYEE_QUERY = {
    excludeDeleted: false,
    includeAutoApprover: false,
    searchValue: undefined,
    companyId: undefined,
}

export const useEmployees = ({ excludeDeleted, includeAutoApprover, searchValue, companyId }: EmployeeQuery = DEFAULT_EMPLOYEE_QUERY) => {
    const currentCompanyId = useCompany()._id
    return useQuery({
        queryKey: [CacheEnum.EMPLOYEES],
        queryFn: ({ signal }) => EmployeeService.fetchAllEmployees({ signal }),
        select: (data) => filterEmployees(data, companyId ?? currentCompanyId, excludeDeleted, includeAutoApprover, searchValue).sort(employeeSorter),
    })
}

export const useLoggedInEmployeeProfile = (): Employee | null | undefined => {
    const { companyId, userId } = useAuth()
    const { data: employee } = useEmployee(userId)
    if (companyId && employee) {
        employee.setActiveCompanyProfile(companyId)
    }
    return employee
}

export const invalidateEmployees = async () => {
    await queryClient.invalidateQueries({
        queryKey: [CacheEnum.EMPLOYEE],
    })
    await queryClient.invalidateQueries({
        queryKey: [CacheEnum.EMPLOYEES],
    })
}
